import { ArrowUpRight } from "lucide-react";
import { PageHeader, Reveal, SectionHead, Label } from "@/components/common/Reveal";
import { CtaBand } from "@/components/sections/CtaBand";
import { CERTIFICATIONS, PROCESS } from "@/data/site";

const Intro = () => (
  <section className="mx-auto grid max-w-7xl gap-14 px-5 py-24 sm:px-8 sm:py-32 lg:grid-cols-12" data-testid="about-intro">
    <Reveal className="lg:col-span-6">
      <Label>Who we are</Label>
      <p className="mt-8 font-display text-3xl font-semibold uppercase leading-tight sm:text-4xl">
        Founded in 2009 by Shane O'Reilly, Ramor Renewables is a mechanical and renewable contractor for construction.
      </p>
      <div className="mt-8 space-y-5 leading-relaxed text-steel sm:text-lg">
        <p>With over 20 years' experience in energy solutions, we have earned a reputation for delivering high-quality workmanship with excellent service.</p>
        <p>Our team of highly experienced, qualified and certified engineers work in partnership with developers and contractors to design and install heating and plumbing solutions for large-scale residential developments. We co-ordinate and manage every project from design through installation to sign-off.</p>
      </div>
    </Reveal>
    <Reveal className="lg:col-span-5 lg:col-start-8" delay={0.1}>
      <div className="corner-ticks relative aspect-[4/5] overflow-hidden border border-canvas-line">
        <img src="/img/projects-hazelbrook-1.webp" alt="Hazelbrook, Churchtown, Dublin 14" className="h-full w-full object-cover" />
        <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_60px_rgba(0,0,0,0.12)]" />
      </div>
      <p className="label-mono mt-3 text-steel">RR-07 · Hazelbrook · 27 houses</p>
    </Reveal>
  </section>
);

const OurPromise = () => (
  <section className="border-y border-canvas-line bg-canvas-800" data-testid="about-promise">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 sm:px-8 sm:py-28 lg:grid-cols-12">
      <div className="lg:col-span-4"><SectionHead label="Our promise" title="On programme. Signed off." /></div>
      <Reveal className="lg:col-span-7 lg:col-start-6">
        <p className="text-xl leading-relaxed text-ink sm:text-2xl">
          We are committed to the highest quality of workmanship, operational excellence and a responsive service to our clients on every scheme — consistently delivered by our team of highly experienced and accredited engineers.
        </p>
        <div className="mt-12 grid gap-px bg-canvas-line sm:grid-cols-3">
          {PROCESS.map((s) => (
            <div key={s.step} className="bg-canvas-800 p-6">
              <span className="label-mono text-brand">{s.step}</span>
              <h3 className="mt-3 font-display text-2xl font-bold uppercase">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-steel">{s.text}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

const Certifications = () => (
  <section className="bg-ink text-canvas cad-grid-light" data-testid="about-certifications">
    <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <SectionHead tone="dark" label="Accreditation" title="Our certifications" />
      <div className="mt-16 grid grid-cols-1 gap-px bg-canvas/15 sm:grid-cols-2 lg:grid-cols-4">
        {CERTIFICATIONS.map((c, i) => (
          <Reveal key={c.code} delay={i * 0.08} className="bg-ink">
            <a href={c.href} target="_blank" rel="noreferrer" className="group flex h-full flex-col p-8" data-testid={`cert-${c.code.toLowerCase()}`}>
              <div className="flex items-start justify-between">
                <span className="font-display text-5xl font-extrabold">{c.code}</span>
                <ArrowUpRight className="h-5 w-5 text-brand-soft transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </div>
              <p className="mt-6 text-sm text-canvas/75">{c.name}</p>
            </a>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHeader testid="about-header" label="About" title="Built on site since 2009"
        text="A Cavan-based mechanical and renewable contractor delivering for developers and main contractors across Dublin and surrounding counties." />
      <Intro />
      <OurPromise />
      <Certifications />
      <CtaBand testid="about-cta" />
    </main>
  );
}
